import {
    EmptyMedical,
    type PartialProfilePermissions,
    type PermissionSection,
    type ProfilePermissions,
    type ReadOnlyProfilePermissions,
    type Section,
} from "@qendo/permissions";
import { ProfileRole, type UserBackedProfileRole } from "@qendo/profile";
import { useEffect, useState } from "react";
import { CheckboxInput } from "~/components/form/CheckboxInput";
import { FieldGroup } from "~/components/form/FieldGroup";
import { PermissionField } from "~/components/form/team/PermissionField";

import { Space } from "~/constants/sizing";
import { Permissions } from "~/entities/profile/permissions";
import { hasCustomMedical, permissionsDiffer } from "~/util/qendo/team";

type PermissionData<R extends UserBackedProfileRole> = R extends ProfileRole.share
    ? ReadOnlyProfilePermissions
    : ProfilePermissions;

export type PermissionEditorProps<R extends UserBackedProfileRole> = {
    value?: PermissionData<R> | PartialProfilePermissions<PermissionData<R>> | null;
    onChangePermissions?: (value: PermissionData<R>) => void;
    disabled?: boolean;
    role?: R;
    editing?: boolean;
    title?: string;
};

export const PermissionEditor = <R extends UserBackedProfileRole = UserBackedProfileRole>({
    value,
    onChangePermissions,
    disabled,
    role,
    editing,
    title = "Permissions",
}: PermissionEditorProps<R>) => {
    const current = new Permissions(value);
    const has_custom_medical = hasCustomMedical(current);
    const [custom, setCustom] = useState(has_custom_medical);

    const mode = editing ? (role === ProfileRole.share ? "share" : "edit") : "display";

    const setPermission = <S extends Section, P extends PermissionSection<S>>(section: S, permission: P) => {
        if (permissionsDiffer(current[section], permission)) {
            onChangePermissions?.(
                new Permissions({
                    ...current,
                    [section]: permission,
                }) as PermissionData<R>,
            );
        }
    };

    const update_custom = (use_custom: boolean) => {
        if (use_custom) {
            setCustom(true);
        } else {
            if (has_custom_medical) {
                onChangePermissions?.(
                    new Permissions({
                        ...current,
                        ...EmptyMedical,
                    }) as PermissionData<R>,
                );
            }

            setCustom(false);
        }
    };

    useEffect(() => {
        if (has_custom_medical && !custom) {
            setCustom(true);
        }
    }, [custom, has_custom_medical]);

    return (
        <>
            <FieldGroup title={title} gap={Space.sm}>
                <PermissionField
                    section="profile"
                    value={current.profile}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="emergency"
                    value={current.emergency}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="team"
                    value={current.team}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="photos"
                    value={current.photos}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="goals"
                    value={current.goals}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="activities"
                    value={current.activities}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="symptoms"
                    value={current.symptoms}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="journal"
                    value={current.journal}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="cycle"
                    value={current.cycle}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="calendar"
                    value={current.calendar}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="documents"
                    value={current.documents}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <PermissionField
                    section="medical"
                    value={current.medical}
                    setPermission={setPermission}
                    disabled={disabled}
                    mode={mode}
                />
                <CheckboxInput
                    label="Customize medical permissions"
                    value={custom}
                    onValueChange={update_custom}
                    disabled={disabled}
                />
            </FieldGroup>
            {custom && (
                <FieldGroup title="Medical permissions" gap={Space.sm}>
                    <PermissionField
                        section="allergies"
                        value={current.allergies}
                        setPermission={setPermission}
                        disabled={disabled}
                        mode={mode}
                    />
                    <PermissionField
                        section="conditions"
                        value={current.conditions}
                        setPermission={setPermission}
                        disabled={disabled}
                        mode={mode}
                    />
                    <PermissionField
                        section="immunisations"
                        value={current.immunisations}
                        setPermission={setPermission}
                        disabled={disabled}
                        mode={mode}
                    />
                    <PermissionField
                        section="medications"
                        value={current.medications}
                        setPermission={setPermission}
                        disabled={disabled}
                        mode={mode}
                    />
                    <PermissionField
                        section="past_symptoms"
                        value={current.past_symptoms}
                        setPermission={setPermission}
                        disabled={disabled}
                        mode={mode}
                    />
                    <PermissionField
                        section="supplements"
                        value={current.supplements}
                        setPermission={setPermission}
                        disabled={disabled}
                        mode={mode}
                    />
                    <PermissionField
                        section="surgeries"
                        value={current.surgeries}
                        setPermission={setPermission}
                        disabled={disabled}
                        mode={mode}
                    />
                    <PermissionField
                        section="triggers"
                        value={current.triggers}
                        setPermission={setPermission}
                        disabled={disabled}
                        mode={mode}
                    />
                </FieldGroup>
            )}
        </>
    );
};
